import { StyleProp, ViewStyle } from 'react-native';
import { NCFileBatchEventsProps } from '@v2/pages/common/components/NCFileBatchEvents';
import { getNcFileTimesConfig } from '@helpers';
import { Position, RawMachineEventData, TextConfig, TimeCategory } from '@interfaces/base';
import { MachineEventSearchItem } from '@interfaces/machines';
import { MachineEventCycles, ProductionBatch } from '@interfaces/search';
import { AccordionRowState } from '@reducers/v2/tableAccordion/tableAccordion.slice';
import { ColorFunction } from '@styles/colors/types';
import { font } from '@styles/fonts';
import { TimeDisplayInStaticFormatProps } from '@components/common/TimeDisplayInStaticFormat';
import { IconConfig, ScaleFunction } from '@components/operatorPanel/interfaces';
import { ClampingDetails, NCFileStatisticsTimesType } from '@v2/context/Times/page/interfaces';
import { NcFileBatchEventType } from '@v2/pages/common/enums';

export interface CycleRawTimes {
  idealActiveTimeInSec?: number;
  idealChangeTimeInSec?: number;
  actualActiveTimeInSec?: number;
  actualChangeTimeInSec?: number;
  scheduledActiveTimeInSec?: number;
  scheduledChangeTimeInSec?: number;
  paidActiveTimeInSec?: number;
  paidChangeTimeInSec?: number;
}

export interface TimeCategoryValues {
  idealTimeInSec: number;
  paidTimeInSec: number;
  actualTimeInSec: number;
  scheduledTimeInSec: number;
}

export interface ProductivityStatusTimes {
  activeTimeInSec: number;
  changeTimeInSec: number;
  setupTimeInSec: number;
  maxTimeInSec: number;
}

export interface EventTimelineData extends TimeCategoryValues, CycleRawTimes, ProductivityStatusTimes {
  showDots: boolean;
}

export type NCFileBatchEvent = MachineEventCycles | ProductionBatch | MachineEventSearchItem;

export interface EventState {
  cycleActiveChangeEvents: MachineEventCycles[];
  setupEvents: ProductionBatch[];
  traceEvents: MachineEventSearchItem[];
}

export interface LoadingState {
  isLoading: boolean;
  error: Error | null;
}

export interface UseTableColumnsParams {
  C: ColorFunction;
  eventType: NcFileBatchEventType;
  shouldDisplayVersion: boolean;
  shouldShowBarChart: boolean;
  shouldDisplayNCFileName: boolean;
  shouldDisplayQuantityDelta: boolean;
  shouldDisplayQuantity: boolean;
  shouldDisplayBatch: boolean;
  shouldDisplayMachine: boolean;
  shouldDisplayAccordionTrigger: boolean;
  shouldDisplayStatusSubStatusExecutionMode: boolean;
  productionBatchId: number | null | undefined;
  eventsPerspective: EventsPerspective | null;
  barChartData: EventTimelineData[];
  textConfig: Record<string, Record<NcFileBatchEventType, (item: MachineEventCycles | ProductionBatch | MachineEventSearchItem) => any>>;
  orderByConfig: Record<string, Partial<Record<NcFileBatchEventType, string>>>;
  orderBy: string;
  scale?: ScaleFunction;
  font?: typeof font;
  // Redux-based accordion state
  expandedRows?: Record<string | number, AccordionRowState<unknown>>;
  toggleAccordion?: (params: { rowKey: string | number; sequenceNumbers?: number[]; productionBatchId?: number; machineId?: number }) => void;
  firstElementKeyValue?: number | null;
}

export interface UseEventBarChartParams extends Pick<UseTableColumnsParams, 'eventsPerspective'> {
  eventType: NcFileBatchEventType;
  events: NCFileBatchEvent[];
  ncFileStatistics: NCFileStatisticsTimesType | null;
}

export interface UseManageNCFileBatchEventsParams
  extends Pick<
    NCFileBatchEventsProps,
    'eventType' | 'ncFileId' | 'productionBatchId' | 'machineIds' | 'startedOn' | 'endedOn' | 'withoutTimeTiles' | 'customOrderBy'
  > {}

export interface FetchEventsParams {
  filter: string | null;
  signal: AbortSignal;
  skip: number;
  currentOrderBy?: string;
}

export enum EventsPerspective {
  SINGLE_MACHINE = 'SingleMachine',
  MULTI_MACHINES = 'MultiMachines',
  NC_FILE = 'NCFile',
  BATCH = 'Batch',
}

export type ParsedMachineEventData = Omit<
  RawMachineEventData,
  | '$type'
  | 'machineId'
  | 'sequenceNumber'
  | 'subStatus'
  | 'executionMode'
  | 'doIgnoreMachineStatusVerification'
  | 'sentFromBackend'
  | 'sentFromCache'
  | 'timestamp'
  | 'tryCount'
  | 'machineCountingDetails'
  | 'ncFiles'
> & {
  machineCountingDetails: Omit<RawMachineEventData['machineCountingDetails'], 'countingMethod'>;
  ncFiles: Array<
    Omit<RawMachineEventData['ncFiles'][number], '$type' | 'transferState' | 'contentHash'> & {
      transferState: Pick<RawMachineEventData['ncFiles'][number]['transferState'], 'transferState'>;
    }
  >;
};

export interface FormattedDataItem {
  key: string;
  value: string | FormattedDataItem[];
  type: 'primitive' | 'object' | 'array';
  indent: number;
}

export interface TimeTileConfig {
  descriptionConfig: TextConfig;
  titleConfig: TextConfig;
  iconConfig: IconConfig;
  timeConfig: Omit<TimeDisplayInStaticFormatProps, 'C'>;
  timeCategory?: TimeCategory;
  editable: boolean;
}

export type NcFileTimesConfig = ReturnType<typeof getNcFileTimesConfig>;
export interface TimeCategoryValuesWithMaxTime extends TimeCategoryValues, CycleRawTimes, Pick<ProductivityStatusTimes, 'maxTimeInSec'> {}

export type OnOptimisticUpdate = (updatedFields: Partial<ClampingDetails>) => void;
export type OnOptimisticUpdateError = () => Promise<void>;

export interface OptimisticUpdateCallbacks {
  onOptimisticUpdate?: OnOptimisticUpdate;
  onOptimisticUpdateError?: OnOptimisticUpdateError;
}

export interface CurveLinesPositions {
  tileDots: Partial<Record<TimeCategory, Position>>;
  markerPositions: Partial<Record<TimeCategory, Position>>;
  containerOffset: Position | null;
}

export interface CurveLinesContextValue {
  positions: CurveLinesPositions;
  measurementVersion: number;
  isResizing: boolean;
  hasSettledOnce: boolean;
  bumpMeasurementVersion: () => void;
  registerTileDot: (type: TimeCategory, position: Position) => void;
  registerMarkerPosition: (type: TimeCategory, position: Position) => void;
  registerContainerOffset: (position: Position) => void;
  resetPositions: () => void;
  resetLinePositions: () => void;
}

export interface CurveLinesContainerProps {
  style?: StyleProp<ViewStyle>;
}
